import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Clock, XCircle, LogOut, RefreshCw } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { supabaseClient } from '../config/supabase'
import toast from 'react-hot-toast'

interface PartnershipRow {
  id: string
  status: string
  rejection_reason?: string
  created_at?: string
  department?: { name: string; abbreviation: string }
}

const EmployerPendingApprovalPage: React.FC = () => {
  const navigate = useNavigate()
  const { user, logout, refreshUser } = useAuthStore()
  const [partnerships, setPartnerships] = useState<PartnershipRow[]>([])
  const [companyName, setCompanyName] = useState('')
  const [approvalStatus, setApprovalStatus] = useState<string>('pending')
  const [loading, setLoading] = useState(true)
  const [checking, setChecking] = useState(false)

  useEffect(() => {
    fetchStatus(false)
  }, [user])

  const fetchStatus = async (showToast = true) => {
    if (!user) {
      navigate('/login/employer')
      return
    }
    try {
      // Fetch employer profile with company info
      const { data: epData, error: epErr } = await supabaseClient
        .from('employer_profiles')
        .select('approval_status, company_id, companies(name)')
        .eq('user_id', user.id)
        .single()

      if (epErr) throw epErr

      setApprovalStatus(epData.approval_status || 'pending')
      setCompanyName((epData.companies as any)?.name || '')

      // Redirect if fully approved
      if (epData.approval_status === 'approved') {
        navigate('/employer-dashboard')
        return
      }

      // Fetch partnership statuses
      if (epData.company_id) {
        const { data: pData } = await supabaseClient
          .from('company_department_partnerships')
          .select('id, status, rejection_reason, created_at, departments(name, abbreviation)')
          .eq('company_id', epData.company_id)
          .order('created_at', { ascending: true })

        setPartnerships(
          (pData || []).map((p: any) => ({
            id: p.id,
            status: p.status,
            rejection_reason: p.rejection_reason,
            created_at: p.created_at,
            department: p.departments,
          }))
        )
      }

      if (showToast) {
        toast('Your partnership request is still under review.', { icon: '⏳' })
      }
    } catch (err: any) {
      console.error('Error fetching employer status:', err)
    } finally {
      setLoading(false)
      setChecking(false)
    }
  }

  const handleCheckStatus = async () => {
    setChecking(true)
    await refreshUser()
    await fetchStatus(true)
  }

  const handleLogout = () => {
    logout()
    navigate('/login/employer')
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    )
  }

  // All partnerships rejected
  if (approvalStatus === 'rejected') {
    const reason = partnerships.find(p => p.status === 'rejected')?.rejection_reason
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundImage: 'url(/uc_nobg.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-2">
              <Link to="/" className="flex items-center">
                <img src="/trailblaze_logo.png" alt="Trailblaze" className="h-20" />
              </Link>
              <nav className="hidden md:flex space-x-8">
                <Link to="/news" className="text-gray-600 hover:text-gray-900">News &amp; Updates</Link>
                <Link to="/#features" className="text-gray-600 hover:text-gray-900">Features</Link>
                <Link to="/#benefits" className="text-gray-600 hover:text-gray-900">Benefits</Link>
                <Link to="/#about" className="text-gray-600 hover:text-gray-900">About UCLM</Link>
              </nav>
              <button onClick={handleLogout} className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors">
                <LogOut className="w-4 h-4" /><span className="text-sm font-medium">Logout</span>
              </button>
            </div>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="bg-white border border-red-200 rounded-2xl shadow-lg p-8">
              <div className="text-center mb-6">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                    <XCircle className="w-10 h-10 text-red-600" />
                  </div>
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Partnership Request Rejected</h1>
                <p className="text-gray-600 text-sm">Your industry partner request for <strong>{companyName}</strong> was not approved.</p>
              </div>
              {reason && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <p className="text-sm font-medium text-red-800 mb-1">Reason provided:</p>
                  <p className="text-sm text-red-700">{reason}</p>
                </div>
              )}
              <div className="space-y-3">
                <p className="text-xs text-gray-500 text-center">Contact UCLM administration if you believe this is an error.</p>
                <button onClick={handleLogout} className="w-full bg-gray-600 hover:bg-gray-700 text-white py-2.5 rounded-lg transition-colors flex items-center justify-center space-x-2">
                  <LogOut className="w-4 h-4" /><span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Pending state
  const pendingCount = partnerships.filter(p => p.status === 'pending').length
  const approvedCount = partnerships.filter(p => p.status === 'approved').length
  const oldestPending = partnerships
    .filter(p => p.status === 'pending' && p.created_at)
    .sort((a, b) => new Date(a.created_at as string).getTime() - new Date(b.created_at as string).getTime())[0]
  const pendingDays = oldestPending?.created_at
    ? Math.floor((Date.now() - new Date(oldestPending.created_at).getTime()) / (1000 * 60 * 60 * 24))
    : 0
  const showFollowUpNote = pendingCount > 0 && pendingDays >= 3

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundImage: 'url(/uc_nobg.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2">
            <Link to="/" className="flex items-center">
              <img src="/trailblaze_logo.png" alt="Trailblaze" className="h-20" />
            </Link>
            <nav className="hidden md:flex space-x-8">
              <Link to="/news" className="text-gray-600 hover:text-gray-900">News &amp; Updates</Link>
              <Link to="/#features" className="text-gray-600 hover:text-gray-900">Features</Link>
              <Link to="/#benefits" className="text-gray-600 hover:text-gray-900">Benefits</Link>
              <Link to="/#about" className="text-gray-600 hover:text-gray-900">About UCLM</Link>
            </nav>
            <button onClick={handleLogout} className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors">
              <LogOut className="w-4 h-4" /><span className="text-sm font-medium">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          <div className="bg-white border border-yellow-200 rounded-2xl shadow-lg p-8">
            <div className="text-center mb-6">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center">
                  <Clock className="w-10 h-10 text-yellow-600" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Partnership Request Pending</h1>
              <p className="text-gray-600 text-sm">
                <strong>{companyName}</strong>'s industry partner request is being reviewed.
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-sm text-yellow-800">
              <p>
                Your request has been sent to the selected department admin(s). You will gain access once all department requests have been reviewed — approval by at least one department is enough to become a partner.
              </p>
              <p className="mt-2 text-xs text-yellow-700">
                Most requests are reviewed within <strong>3-5 days</strong>. If it takes longer, please contact the related department(s) for follow-up.
              </p>
              {approvedCount > 0 && pendingCount > 0 && (
                <p className="mt-2 font-medium text-green-700">
                  ✓ {approvedCount} department{approvedCount > 1 ? 's' : ''} approved — {pendingCount} still pending.
                </p>
              )}
              {approvedCount > 0 && pendingCount === 0 && (
                <p className="mt-2 font-medium text-green-700">
                  ✓ All requests reviewed. Click <strong>Refresh Status</strong> to continue.
                </p>
              )}
              {showFollowUpNote && (
                <p className="mt-2 font-medium text-orange-700">
                  Your request has been pending for {pendingDays} day{pendingDays !== 1 ? 's' : ''}. If it is not approved within 3-5 days, please contact the related department(s) for follow-up.
                </p>
              )}
            </div>

            {/* Partnership status breakdown */}
            {partnerships.length > 0 && (
              <div className="mb-6">
                <p className="text-sm font-semibold text-gray-700 mb-3">Department Requests</p>
                <div className="space-y-2">
                  {partnerships.map(p => (
                    <div key={p.id} className="flex items-center justify-between px-3 py-2 rounded-lg border border-gray-100 bg-gray-50">
                      <span className="text-sm text-gray-700">{p.department?.name ?? '—'}</span>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                        p.status === 'approved'
                          ? 'bg-green-100 text-green-700'
                          : p.status === 'rejected'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={handleCheckStatus}
                disabled={checking}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-lg transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`} />
                <span>{checking ? 'Checking...' : 'Refresh Status'}</span>
              </button>
              <button
                onClick={handleLogout}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 rounded-lg transition-colors flex items-center justify-center space-x-2"
              >
                <LogOut className="w-4 h-4" /><span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EmployerPendingApprovalPage
